import {FormRepository, ScoreCalculator} from 'domain/ports';
import {Question, Section} from 'domain/entities';

/**
 * Servicio para manejar la lógica del formulario
 */
export class FormService {
    constructor(
        private formRepository: FormRepository,
        private scoreCalculator: ScoreCalculator
    ) {
    }

    /**
     * Obtiene las secciones de una guía específica
     * @param formKey Clave de la guía
     */
    async getSections(formKey: string): Promise<Section[]> {
        return this.formRepository.getSections(formKey);
    }

    /**
     * Obtiene las preguntas de una guía específica
     * @param formKey Clave de la guía
     */
    async getQuestions(formKey: string): Promise<Question[]> {
        return this.formRepository.getQuestions(formKey);
    }

    /**
     * Obtiene la escala de valores para una guía específica
     * @param formKey Clave de la guía
     */
    async getScale(formKey: string): Promise<{ [key: string]: number } | undefined> {
        const guide = await this.formRepository.getGuideByKey(formKey);
        return guide?.scale;
    }

    /**
     * Guarda una respuesta y calcula su puntuación
     * @param guiaKey Clave principal de la guía
     * @param formKey Clave de la guía específica
     * @param questionId ID de la pregunta
     * @param value Valor de la respuesta
     */
    async saveAnswer(guiaKey: string, formKey: string, questionId: string, value: any): Promise<void> {
        // Obtener respuestas actuales
        const currentAnswers = await this.formRepository.getAnswers(guiaKey, formKey) || {};

        // Actualizar respuesta
        const newAnswers = {
            ...currentAnswers,
            [questionId]: value,
        };

        // Guardar respuestas actualizadas
        await this.formRepository.saveAnswers(guiaKey, formKey, newAnswers);

        // Calcular y guardar puntuación
        await this.calculateAndSaveScore(guiaKey, formKey, questionId, value);
    }

    /**
     * Obtiene todas las preguntas de una guía, incluyendo las dependientes
     * @param formKey Clave de la guía
     */
    async getAllQuestions(formKey: string): Promise<Question[]> {
        const guide = await this.formRepository.getGuideByKey(formKey);
        if (!guide) return [];

        let allQuestions: Question[] = guide.questions || [];

        // Añadir preguntas dependientes de serviceToClients si existen
        if (guide.serviceToClients?.dependentQuestions) {
            allQuestions = [...allQuestions, ...guide.serviceToClients.dependentQuestions];
        }

        // Añadir preguntas dependientes de isBoss si existe (ahora como propiedad independiente)
        if (guide.isBoss?.dependentQuestions) {
            allQuestions = [...allQuestions, ...guide.isBoss.dependentQuestions];
        }

        return allQuestions;
    }

    /**
     * Resetea las respuestas dependientes cuando se selecciona "No" en preguntas clave
     * @param guiaKey Clave principal de la guía
     * @param formKey Clave de la guía específica
     * @param questionId ID de la pregunta
     * @param value Valor de la respuesta
     */
    async resetDependentQuestions(guiaKey: string, formKey: string, questionId: string, value: any): Promise<void> {
        if (value !== false) return;

        const guide = await this.formRepository.getGuideByKey(formKey);
        if (!guide) return;

        const serviceKey = `${formKey}_serviceToClients`;
        const isBossKey = `${formKey}_isBoss`;

        // Resetear preguntas dependientes de serviceToClients
        if (questionId === serviceKey && guide.serviceToClients?.dependentQuestions) {
            const [currentAnswers, currentScores] = await Promise.all([
                this.formRepository.getAnswers(guiaKey, formKey) || {},
                this.formRepository.getScores(guiaKey, formKey) || {}
            ]);

            const newAnswers = {...currentAnswers};
            const newScores = {...currentScores};

            // Resetear cada pregunta dependiente
            guide.serviceToClients.dependentQuestions.forEach(q => {
                newAnswers[q.id] = undefined;
                newScores[q.id] = 0;
            });

            // Guardar cambios
            await Promise.all([
                this.formRepository.saveAnswers(guiaKey, formKey, newAnswers),
                this.formRepository.saveScores(guiaKey, formKey, newScores)
            ]);
        }

        // Resetear preguntas dependientes de isBoss
        if (questionId === isBossKey && guide.isBoss?.dependentQuestions) {
            const [currentAnswers, currentScores] = await Promise.all([
                this.formRepository.getAnswers(guiaKey, formKey) || {},
                this.formRepository.getScores(guiaKey, formKey) || {}
            ]);

            const newAnswers = {...currentAnswers};
            const newScores = {...currentScores};

            // Resetear cada pregunta dependiente
            guide.isBoss.dependentQuestions.forEach(q => {
                newAnswers[q.id] = undefined;
                newScores[q.id] = 0;
            });

            // Guardar cambios
            await Promise.all([
                this.formRepository.saveAnswers(guiaKey, formKey, newAnswers),
                this.formRepository.saveScores(guiaKey, formKey, newScores)
            ]);
        }

    }

    /**
     * Verifica si una sección debe ser visible según sus condiciones
     * @param section Sección a verificar
     * @param answers Respuestas actuales
     */
    isSectionVisible(section: Section, answers: { [key: string]: any }): boolean {
        if (!section.condition) {
            return true;
        }

        const {type, questionIds} = section.condition;

        if (type === 'any') {
            return questionIds.some(questionId => answers[questionId]);
        }

        return false;
    }

    /**
     * Calcula y guarda la puntuación para una respuesta
     * @param guiaKey Clave principal de la guía
     * @param formKey Clave de la guía específica
     * @param questionId ID de la pregunta
     * @param value Valor de la respuesta
     */
    private async calculateAndSaveScore(guiaKey: string, formKey: string, questionId: string, value: any): Promise<void> {
        // Obtener puntuaciones actuales
        const currentScores = await this.formRepository.getScores(guiaKey, formKey) || {};

        // Buscar la pregunta
        const questions = await this.getAllQuestions(formKey);
        const question = questions.find(q => q.id === questionId);

        if (question) {
            // Calcular puntuación
            const scale = await this.getScale(formKey);
            const calculatedScore = this.scoreCalculator.calculateScore(question, value, scale);

            // Actualizar puntuaciones
            const newScores = {
                ...currentScores,
                [questionId]: calculatedScore,
            };

            // Guardar puntuaciones actualizadas
            await this.formRepository.saveScores(guiaKey, formKey, newScores);
        }
    }
}
